<template>
    <div class="page vendor-page">
        <div class="page-header">
            <div class="page-header-content">
                <h1>廠商管理</h1>
                <a-button type="primary" @click="openCreate">新增廠商</a-button>
            </div>
        </div>

        <div class="content">
        <a-table
            :columns="columns"
            :data-source="rows"
            :loading="loading"
            row-key="id"
            :pagination="false"
        >
            <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'kind'">
                    {{ record.is_house ? '自營' : '寄賣' }}
                </template>
                <template v-else-if="column.key === 'email'">
                    {{ record.email || '—' }}
                </template>
                <template v-else-if="column.key === 'action'">
                    <a-button type="link" @click="openEdit(record)">編輯</a-button>
                </template>
            </template>
        </a-table>
        </div>

        <a-modal
            v-model:open="modalOpen"
            :title="editing?.is_house ? '世界樹' : editing ? '編輯廠商' : '新增廠商'"
            :confirm-loading="saving"
            @ok="save"
        >
            <a-form layout="vertical">
                <a-form-item label="名稱">
                    <a-input
                        v-model:value="form.name"
                        :disabled="editing?.is_house"
                        placeholder="廠商名稱"
                    />
                </a-form-item>
                <a-form-item label="通知信箱">
                    <a-input
                        v-model:value="form.email"
                        placeholder="寄賣廠商必填，世界樹可留空"
                    />
                </a-form-item>
            </a-form>
        </a-modal>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { message } from 'ant-design-vue';
import { createVendor, listVendors, updateVendor } from '@/api/products';

const rows = ref([]);
const loading = ref(false);
const saving = ref(false);
const modalOpen = ref(false);
const editing = ref(null);
const form = ref({ name: '', email: '' });

const columns = [
    { title: '名稱', dataIndex: 'name', key: 'name' },
    { title: '類型', key: 'kind' },
    { title: '通知信箱', key: 'email' },
    { title: '', key: 'action', width: 100 }
];

const load = async () => {
    loading.value = true;
    try {
        const response = await listVendors();
        rows.value = response?.result ? response.data || [] : [];
        if (!response?.result) {
            message.error(response?.message || '載入廠商失敗');
        }
    } catch {
        message.error('載入廠商失敗');
    } finally {
        loading.value = false;
    }
};

const openCreate = () => {
    editing.value = null;
    form.value = { name: '', email: '' };
    modalOpen.value = true;
};

const openEdit = (record) => {
    editing.value = record;
    form.value = { name: record.name, email: record.email || '' };
    modalOpen.value = true;
};

const save = async () => {
    const name = form.value.name.trim();
    const email = form.value.email.trim();
    if (!editing.value?.is_house && !name) {
        message.error('請填名稱');
        return;
    }
    if (!editing.value?.is_house && !email) {
        message.error('寄賣廠商要填通知信箱');
        return;
    }
    saving.value = true;
    try {
        const payload = { name, email: email || null };
        const response = editing.value
            ? await updateVendor(editing.value.id, payload)
            : await createVendor(payload);
        if (!response?.result) {
            message.error(response?.message || '儲存失敗');
            return;
        }
        message.success('已儲存');
        modalOpen.value = false;
        await load();
    } catch {
        message.error('儲存失敗');
    } finally {
        saving.value = false;
    }
};

onMounted(load);
</script>

<style scoped>
.vendor-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
}

.content {
    background: #ffffff;
    border: 1px solid rgba(138, 175, 255, 0.15);
    border-radius: 12px;
    padding: 24px;
    min-height: 400px;
    box-shadow: 0 2px 12px rgba(138, 175, 255, 0.08);
}
</style>
